import {instance, noTokenInterceptor} from "./InterceptorApi";
import {AxiosError, AxiosRequestConfig, AxiosResponse} from "axios";

type RequestParams = {
    [key: string]: any;
};

const requestUtil = {
    handleRequest: (response: AxiosResponse<any>): { data: any; response: AxiosResponse<any> } => {
        const contentType = response.headers["content-type"] as string;
        if (contentType && typeof contentType === "string" && contentType.includes("application/json")) {
            return {
                data: response.data,
                response: response,
            };
        } else if (contentType && typeof contentType === "string" && contentType.includes("text/plain")) {
            return {
                data: response.data,
                response: response,
            };
        }
            // else if (contentType && contentType.includes("text/xml")) {
            //     return response?.blob().then(data => ({
            //         data: data,
            //         response: response,
            //     }));
        // }

        else if (contentType && typeof contentType === "string" && contentType.includes("application/pdf")) {
            return {
                data: response.data,
                response: response,
            };
        } else {
            return {
                data: undefined,
                response: response,
            };
        }
    },
    errorHandling: (error: AxiosError): { data: { type: string; payload: { message: string } }; response: AxiosResponse<any> | undefined } => {
        const responseData = error.response?.data as any;
        const backendMessage =
            responseData?.message ||
            responseData?.error ||
            responseData?.payload?.message ||
            null;
        return {
            data: {
                type: "serverError",
                payload: {
                    message: backendMessage || "An unexpected error occurred. Please try again.",
                },
            },
            response: error.response,
        };
    }
};

/**
 * Every helper in `requests` resolves — even for HTTP errors, which are mapped to
 * `{data: {type: 'serverError', payload: {message}}}` by `errorHandling` above.
 * That means `try/catch` and `.catch()` around these calls never fire, so callers
 * must ask explicitly whether the call failed before treating the result as data.
 */
export const isRequestError = (result: any): boolean => {
    if (!result) return true;
    const status = result.response?.status;
    if (typeof status === 'number') return status < 200 || status >= 300;
    return result.data?.type === 'serverError';
};

export const getRequestErrorMessage = (
    result: any,
    fallback = 'An unexpected error occurred. Please try again.'
): string => result?.data?.payload?.message || fallback;

const requests = {
    getBlob: (url: string) => {
        return instance
            .get(url, {responseType: 'arraybuffer'})
            .then((response: AxiosResponse<any, any>) => ({
                data: response.data as ArrayBuffer,
                contentType: response.headers['content-type'] as string,
                response,
            }))
            .catch(error => requestUtil.errorHandling(error));
    },
    get: (url: string) => {
        return instance
            .get(url)
            .then((response: AxiosResponse<any, any>) => requestUtil.handleRequest(response))
            .catch((error: AxiosError<unknown, any>) => requestUtil.errorHandling(error));
    },
    getNoToken: (url: string) => {
        return noTokenInterceptor
            .get(url)
            .then((response: AxiosResponse<any, any>) => requestUtil.handleRequest(response))
            .catch((error: AxiosError<unknown, any>) => requestUtil.errorHandling(error));
    },
    getNoTokenFresh: (url: string) => {
        return noTokenInterceptor
            .get(url, {
                params: {_cacheBust: Date.now()},
                headers: {
                    "Cache-Control": "no-cache, no-store",
                    Pragma: "no-cache",
                },
            })
            .then((response: AxiosResponse<any, any>) => requestUtil.handleRequest(response))
            .catch((error: AxiosError<unknown, any>) => requestUtil.errorHandling(error));
    },
    getV2: (url: string) => {
        return instance
            .get(url, {responseType: 'arraybuffer'})
            .then((response: AxiosResponse<any, any>) => requestUtil.handleRequest(response))
            .catch(error => requestUtil.errorHandling(error));
    },
    post: (url: string, param: RequestParams) => {
        return instance
            .post(url, param)
            .then((response: AxiosResponse<any, any>) => requestUtil.handleRequest(response))
            .catch((error: AxiosError<unknown, any>) => requestUtil.errorHandling(error));
    },
    postNoToken: (url: string, param: RequestParams) => {
        return noTokenInterceptor
            .post(url, param)
            .then(response => requestUtil.handleRequest(response))
            .catch(error => requestUtil.errorHandling(error));
    },
    postV2: (url: string, param: RequestParams) => {
        return instance
            .post(url, param, {responseType: 'arraybuffer'})
            .then((response: AxiosResponse<any, any>) => requestUtil.handleRequest(response))
            .catch((error: AxiosError<unknown, any>) => requestUtil.errorHandling(error));
    },
    postV3: (url: string, param: RequestParams) => {
        return instance
            .post(url, param, {responseType: 'arraybuffer'})
            .then((response: AxiosResponse<any, any>) => requestUtil.handleRequest(response))
            .catch((error: AxiosError<unknown, any>) => requestUtil.errorHandling(error));
    },
    postMultipart: (url: string, param: FormData) => {
        const config = {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        };
        return instance
            .post(url, param, config)
            .then((response: AxiosResponse<any, any>) => requestUtil.handleRequest(response))
            .catch((error: AxiosError<unknown, any>) => requestUtil.errorHandling(error));
    },
    put: (url: string, param: RequestParams) => {
        return instance
            .put(url, param)
            .then((response: AxiosResponse<any, any>) => requestUtil.handleRequest(response))
            .catch((error: AxiosError<unknown, any>) => requestUtil.errorHandling(error));
    },
    patch: (url: string, param: RequestParams) => {
        return instance
            .patch(url, param)
            .then((response: AxiosResponse<any, any>) => requestUtil.handleRequest(response))
            .catch((error: AxiosError<unknown, any>) => requestUtil.errorHandling(error));
    },
    delete: (url: string) => {
        return instance
            .delete(url)
            .then((response: AxiosResponse<any, any>) => requestUtil.handleRequest(response))
            .catch((error: AxiosError<unknown, any>) => requestUtil.errorHandling(error));
    },
    deleteWithRequestBody: (url: string, data: any, config?: AxiosRequestConfig) => {
        return instance
            .delete(url, {data, ...config}) // Send request body in DELETE request
            .then((response: AxiosResponse<any, any>) => requestUtil.handleRequest(response))
            .catch((error: AxiosError<unknown, any>) => requestUtil.errorHandling(error));
    },
    postWithHttpParams: (url: string, param: RequestParams) => {
        return instance
            .post(url, null, {params: param})
            .then((response: AxiosResponse<any, any>) => requestUtil.handleRequest(response))
            .catch((error: AxiosError<unknown, any>) => requestUtil.errorHandling(error));
    },
    getWithHttpParams: (url: string, param: RequestParams) => {
        return instance
            .get(url, {params: param})
            .then((response: AxiosResponse<any, any>) => requestUtil.handleRequest(response))
            .catch((error: AxiosError<unknown, any>) => requestUtil.errorHandling(error));
    },
};

export default {requests};
